import Vue from "vue";
import * as storeHelper from "@/shared/storeHelper";
import * as UserService from "@/shared/api/userService";
import {
    FetchState,
    AccountAlertState,
    AccountAlert
} from "@/shared/types";
import { defineStore } from "pinia";
import _ from "lodash";

export const initialState: AccountAlertState = {
    fetchState: FetchState.Uninitialized,
    alertUpdateFetchState: FetchState.Loaded,
    accountAlertsDictionary: {},
    panelOpen: false
};


export const useAccountAlertStore = defineStore("accountAlert", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        accountAlerts: state => _.orderBy(state.accountAlertsDictionary, "dateCreated", "desc"),
        unreadAccountAlerts: state => _.filter(state.accountAlertsDictionary, [ "isRead", false ]),
        unreadAccountAlertsCount: state => _.filter(state.accountAlertsDictionary, [ "isRead", false ]).length,
    },
    actions: {
        setStateLoading (): void {
            this.fetchState = FetchState.Loading;
        },
        setStateLoaded (): void {
            this.fetchState = FetchState.Loaded;
        },
        setAlertUpdateLoading (): void {
            this.alertUpdateFetchState = FetchState.Loading;
        },
        setAlertUpdateLoaded (): void {
            this.alertUpdateFetchState = FetchState.Loaded;
        },
        setAccountAlerts (data: { messages: AccountAlert[] }): void {
            data.messages.forEach(accountAlert => {
                Vue.set(this.accountAlertsDictionary, `${accountAlert.notificationId}`, accountAlert);
            });
        },
        setAccountAlertRead (notificationId: number): void {
            const selectedAlert = this.accountAlertsDictionary[notificationId];
            if (selectedAlert) {
                selectedAlert.isRead = true;
                Vue.set(this.accountAlertsDictionary, `${notificationId}`, selectedAlert);
            }
        },
        setAllAccountAlertsRead (): void {
            const updatedAccountAlertsDictionary = _.forEach(this.accountAlertsDictionary,
                alert => alert.isRead = true);
            Vue.set(this, "accountAlertsDictionary", updatedAccountAlertsDictionary);
        },
        togglePanelOpen (): void {
            this.panelOpen = !this.panelOpen;
        },
        loadAccountAlerts () {
            if (this.fetchState === FetchState.Uninitialized) {
                this.setStateLoading();

                UserService.getUserAccountAlerts().then(response => {
                    this.setStateLoaded();
                    this.setAccountAlerts(response.data);
                }).catch(err => {
                    this.setStateLoaded();
                    storeHelper.handleError({ error: err, rethrow: true });
                });
            }
        },
        setAccountAlertsRead (notificationId: number = undefined) {
            if (this.alertUpdateFetchState === FetchState.Loaded) {
                this.setAlertUpdateLoading();

                let requestValues = null;
                let updateAllValues = true;
                if (notificationId) {
                    updateAllValues = false;
                    requestValues = [ notificationId ];
                } else {
                    requestValues = _.map(this.accountAlertsDictionary, "notificationId");
                }

                UserService.setUserAccountAlertsRead(requestValues).then(response => {
                    this.setAlertUpdateLoaded();
                    updateAllValues ? this.setAllAccountAlertsRead() : this.setAccountAlertRead(notificationId);
                }).catch(err => {
                    this.setAlertUpdateLoaded();
                    storeHelper.handleError({ error: err, rethrow: true });
                });
            }
        },
        deleteAccountAlert (notificationId: number) {
            if (this.alertUpdateFetchState === FetchState.Loaded) {
                this.setAlertUpdateLoading();

                UserService.deleteUserAccountAlert(notificationId).then(() => {
                    const updatedAccountAlertsDictionary = _.filter(this.accountAlertsDictionary,
                        alert => alert.notificationId !== notificationId);

                    Vue.set(this, "accountAlertsDictionary", updatedAccountAlertsDictionary);
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                }).finally(() => this.setAlertUpdateLoaded());
            }
        }
    }
});

